import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  DocumentReference,
  DocumentSnapshot,
  FieldValue,
  Query,
} from "@atgof-firebase/firebase";
import FieldsRow from "../components/fields/FieldsRow";
import { Field } from "../common/helpers/fields";
import { RootStackParamList } from "../types";
import React, { useContext, useMemo } from "react";
import NavPage from "../components/pages/NavPage";
import { useSheetsCollectionSpec } from "../data/useSheetsCollectionSpec";
import CollectionList, {
  useCollectionItems,
} from "../components/CollectionList";
import { ScriptButton } from "../components/ScriptButton";
import PASceneInfo from "../components/PASceneInfo";
import Select from "../components/Select";
import {
  compareScenes,
  Scene,
  sceneLabel,
  sortScenes,
  toScene,
} from "../common/scene";
import { LanguageContext } from "../common/language";
import Labelled from "../components/LabelledSelect";
import { ProjectContext } from "../data/projectContext";
import { useDocument } from "../data/useDocument";
import { useDocumentReference } from "../data/firestore";
import SceneInfo from "../components/SceneInfo";
import { PostProductionSceneOptions } from "../components/PostProductionSceneOptions";
import { View } from "react-native";
import {
  textForEvent,
  TextInput,
  TextInputEvent,
} from "../components/fields/TextInput";
import { MembershipCategory } from "@atgof-common/model/project/membership";
import { Text } from "react-native-paper";
import { DateEditor } from "../components/DateEditor";
import { ListRenderItemInfo } from "@shopify/flash-list";

const ROWS: Field[][] = [
  [
    {
      key: "isComplete",
      kind: { flags: [{ key: "isComplete", labelKey: "" }] },
    },
    { key: "isDropped", kind: { flags: [{ key: "isDropped", labelKey: "" }] } },
    {
      key: "actions",
      kind: {
        composite: [
          { key: "isDone", kind: { flags: [{ key: "isDone", labelKey: "" }] } },
          { key: "description", kind: "longText" },
        ],
      },
      multi: { explicitAdd: {} },
    },
  ],
  /*[
      {
          key: "set", isTitle: true,
          kind: {
              reference: { collection: "sets" }
          },
      },
      {
          key: "cast", multi: {},
          kind: {
              reference: { collection: "characters" }
          },
      },
  ],
  [
      { key: "synopsis", kind: 'longText' },
  ],
  [
      {
          labelKey: "int-ext",
          kind: {
              flags: [{ key: "isInterior", labelKey: "int" }, { key: "isExterior", labelKey: "ext" }]
          }
      },
      { key: "timeOfDay", labelKey: "tod", kind: { select: ["day", "night", "dawn", "dusk"] } },
      { key: "pages", kind: 'shortText' },
  ],
  [
      { key: "notes", kind: 'paragraph' },
      { key: "extras", multi: {} },
  ]*/
];

function indexInFilmingSequence(
  sceneRef: DocumentReference | undefined,
  sequence: DocumentReference[] | undefined,
) {
  if (!(sceneRef && sequence)) return -1;
  return sequence.findIndex((ref) => ref.path === sceneRef.path);
}

function nextInFilmingSequence(
  sceneRef: DocumentReference | undefined,
  sequence: DocumentReference[] | undefined,
) {
  const i = indexInFilmingSequence(sceneRef, sequence);
  if (sequence && i >= 0 && i + 1 < sequence.length) return sequence[i + 1];
  return;
}

async function updateFilmingSequence(
  sceneRef: DocumentReference | undefined,
  runsIntoSceneRef: DocumentReference | undefined,
  sequence: DocumentReference[] | undefined,
) {
  if (!sceneRef) return;
  let seq = sequence ? [...sequence] : [];
  let i = indexInFilmingSequence(sceneRef, seq);
  if (i < 0) {
    seq.push(sceneRef);
    i = seq.length - 1;
  }
  const origTail = seq.slice(i + 1);
  const head = seq.slice(0, i + 1);
  const runsIntoScene = await runsIntoSceneRef?.get();
  const runsIntoSeq: DocumentReference[] =
    runsIntoScene?.get("continuousFilmingSequence") ?? [];
  const runsIntoI = indexInFilmingSequence(runsIntoSceneRef, runsIntoSeq);
  const newTail =
    runsIntoI < 0
      ? runsIntoSceneRef
        ? [runsIntoSceneRef]
        : []
      : runsIntoSeq.slice(runsIntoI);
  const newSeq = [...head, ...newTail];
  for (const ref of newSeq) ref.update({ continuousFilmingSequence: newSeq });
  for (const ref of origTail) {
    if (!newTail.find((r) => r.path === ref.path))
      ref.update({ continuousFilmingSequence: FieldValue.delete() });
  }
}

function PANotesField({
  scene,
  readonly,
}: {
  scene: DocumentSnapshot | undefined;
  readonly: boolean;
}) {
  const { ph } = useContext(LanguageContext);
  const value = scene?.get("paNotes") ?? "";
  const update = async (evt: TextInputEvent) => {
    const text = textForEvent(evt);
    if (typeof text === "string") {
      await scene?.ref.update({ paNotes: text });
    }
  };
  if (readonly && !value.length) return null;
  return (
    <Labelled label={ph("notes") as string}>
      {readonly ? (
        <Text>{value}</Text>
      ) : (
        <TextInput defaultValue={value} onBlur={update} onEndEditing={update} />
      )}
    </Labelled>
  );
}

function AdminSettings({
  doc,
  scene,
  categories,
}: {
  doc: DocumentSnapshot | undefined;
  scene: Scene | undefined;
  categories: MembershipCategory[];
}) {
  const { ph } = React.useContext(LanguageContext);
  const collSpec = React.useMemo(() => {
    const collection = scene?.ref.parent;
    return collection
      ? {
          collection,
          process: (snapshots: DocumentSnapshot[]) =>
            snapshots.map((snapshot) => toScene(snapshot)),
        }
      : undefined;
  }, [scene?.ref.parent.path]);
  const scenes = useCollectionItems(collSpec);
  const selectableScenes = React.useMemo(
    () =>
      scene && scenes
        ? sortScenes(
            scenes.filter((otherScene) => compareScenes(scene, otherScene) < 0),
          ).map(({ ref }) => ref)
        : undefined,
    [scene, scenes],
  );
  const sequence = scene?.continuousFilmingSequence;
  const sequenceStr = React.useMemo(
    () => JSON.stringify(sequence?.map((ref) => ref.path)),
    [sequence],
  );
  const selectedEntry = React.useMemo(
    () => nextInFilmingSequence(scene?.ref, sequence),
    [scene?.ref.path, sequenceStr],
  );
  const onEntryChange = React.useCallback(
    (ref: DocumentReference | undefined) =>
      updateFilmingSequence(scene?.ref, ref, sequence),
    [scene?.ref.path, sequenceStr],
  );
  const updateFilmingDate = (date: Date | undefined) => {
    scene?.ref.update({ filmingDate: date ?? FieldValue.delete() });
  };
  return (
    <React.Fragment>
      <View style={{ flexDirection: "row", gap: 16 }}>
        <DateEditor
          label={ph("filming-date") as string}
          value={scene?.filmingDate}
          onChange={updateFilmingDate}
        />
        {scene?.baseSceneId && scene.baseSceneId !== scene.id ? (
          <Labelled label={ph("part-of") as string}>
            <Text style={{ fontWeight: "bold" }}>
              {sceneLabel(scene.ref.parent.doc(scene.baseSceneId).path)}
            </Text>
          </Labelled>
        ) : null}
        <Labelled label={ph("runs-into") as string}>
          <Select
            entries={selectableScenes}
            entryKey={(ref) => ref?.id ?? ""}
            entryLabel={(ref) => ref.id}
            onEntryChange={onEntryChange}
            selectedEntry={selectedEntry}
            includeNone
            highlightUnselected={false}
            resetSelectionIfNotPresent={false}
          />
        </Labelled>
        <PANotesField scene={doc} readonly={!categories.includes("pa")} />
      </View>
      {ROWS.map((fields, i) => (
        <FieldsRow key={i} doc={doc} fields={fields} />
      ))}
    </React.Fragment>
  );
}

type HeaderItem =
  | { scene: Scene; excludeFilmingDate: boolean }
  | {
      doc: DocumentSnapshot | undefined;
      scene: Scene | undefined;
      categories: MembershipCategory[];
    }
  | { scene: Scene }
  | { sceneRef: DocumentReference | undefined };

function renderHeaderItem(info: ListRenderItemInfo<HeaderItem>) {
  const { item } = info;
  const elt =
    "doc" in item ? (
      <AdminSettings
        doc={item.doc}
        scene={item.scene}
        categories={item.categories}
      />
    ) : "excludeFilmingDate" in item ? (
      <SceneInfo
        scene={item.scene}
        excludeFilmingDate={item.excludeFilmingDate}
      />
    ) : "scene" in item ? (
      <PostProductionSceneOptions scene={item.scene} horizontal />
    ) : (
      <PASceneInfo scene={item.sceneRef} />
    );
  return <View style={{ marginBottom: 10 }}>{elt}</View>;
}

export default function SceneScreen({
  route,
}: NativeStackScreenProps<RootStackParamList, "scene">) {
  const { scenePath } = route.params;
  const { categories } = React.useContext(ProjectContext);
  const scene = useDocument(useDocumentReference(scenePath));
  const addConstraints = React.useCallback(
    (q: Query) => (scene ? q.where("scene", "==", scene.ref) : undefined),
    [scene?.ref.path],
  );
  const sheetsCollection = useSheetsCollectionSpec({ addConstraints });
  const isPA = categories.includes("pa");
  const isPostProd = categories.includes("postProduction");
  const showAdminSettings = React.useMemo(
    () => isPA || isPostProd || categories.includes("admin"),
    [isPA, isPostProd, categories],
  );
  const scene_ = useMemo(() => (scene ? toScene(scene) : undefined), [scene]);
  const headerItems = useMemo<HeaderItem[]>(
    () => [
      ...(scene_
        ? [{ scene: scene_, excludeFilmingDate: showAdminSettings }]
        : []),
      ...(showAdminSettings ? [{ doc: scene, scene: scene_, categories }] : []),
      ...(scene_ && isPostProd ? [{ scene: scene_ }] : []),
      ...(isPA ? [{ sceneRef: scene?.ref }] : []),
    ],
    [scene_, showAdminSettings, scene, categories, isPostProd],
  );
  return (
    <NavPage
      fullScreen
      isLoading={!sheetsCollection}
      headerRight={<ScriptButton parent={scene_} />}
      newSheetContext={{ sceneRef: scene?.ref }}
    >
      {sheetsCollection && (
        <CollectionList
          collections={[sheetsCollection]}
          headerItems={headerItems}
          renderHeaderItem={renderHeaderItem}
          estimatedItemSize={85}
        />
      )}
    </NavPage>
  );
}
